import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { yourPlace, type ArticleView, type SearchConsoleView, type SearchTermView, type SeoResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon } from "../ui/icons.js";
import { Card } from "./chrome.js";

/**
 * What Google says about this site
 * ([21 §13](../../../../docs/21-articles-and-seo.md#13--what-google-search-console-says)).
 *
 * The rest of the SEO tab is about writing. This is the only part that knows whether any of it worked, and the
 * only numbers in the whole tool that are not a model's opinion — they are Google's, about the customer's own
 * site, read with the customer's own grant.
 *
 * Two things the screen must keep saying. Every window ends three days ago, because Search Console is always
 * behind and a chart that runs to today shows a collapse made of missing data. And *what to write next* is the
 * opportunity list, not a guess: phrases Google **already** shows this site for, on page two, that nobody clicks.
 */
export function GoogleSays({ seo, onChanged, onConnection }: {
  seo: SeoResponse;
  onChanged: () => void;
  /** Where the connection is kept, Business → SEO, for the one line under the numbers that points there. */
  onConnection?: (() => void) | undefined;
}) {
  const g = seo.google;
  // Where the live articles come needs nothing from Google's side, so it is here whatever state Search Console is in.
  const places = <WhereYouCome seo={seo} onChanged={onChanged} />;
  if (!g) {
    return (
      <section className="flex flex-col gap-3">
        {places}
        <Panel>
          <p className="text-[13px] font-semibold">Google Search Console is not available yet.</p>
          <p className="text-[12.5px] text-muted mt-0.5 leading-relaxed">It needs a Google client, which this server has not been given.</p>
        </Panel>
      </section>
    );
  }
  if (!g.connected) return <section className="flex flex-col gap-3">{places}<Connect onChanged={onChanged} /></section>;
  if (!g.property) return <section className="flex flex-col gap-3">{places}<PickProperty onChanged={onChanged} account={g.account} /></section>;
  return <Numbers g={g} places={places} onChanged={onChanged} onConnection={onConnection} />;
}

/** The articles that are out, which are the ones where a place means anything. */
export const liveArticles = (seo: SeoResponse): ArticleView[] =>
  seo.articles.filter((a) => a.status !== "dropped" && a.editions.some((e) => e.status === "published" || e.status === "updated"));

/**
 * Where each live article comes for its phrase — the one ranking a site gets without Search Console
 * ([21 §13](../../../../docs/21-articles-and-seo.md#13--what-google-search-console-says)).
 *
 * It is one search from one country on one day, found by the business's address in the ten results: not Google's
 * average over a month, and the screen says which of the two it is. A search read before the article went out is
 * about a page that did not exist yet, so it says that too rather than showing it as a verdict.
 */
function WhereYouCome({ seo, onChanged }: { seo: SeoResponse; onChanged: () => void }) {
  const live = liveArticles(seo);
  const m = useMutation({ mutationFn: () => api.readAllSerps(), onSuccess: onChanged });
  if (!live.length) return null;
  const busy = m.isPending || seo.working.includes("read_serp");
  const price = (live.length * Number(seo.prices.readSerp)).toFixed(4);
  const address = seo.address ?? seo.site?.baseUrl;
  return (
    <Card
      title="Where your articles come"
      note="one Google search each, by Joogoos"
      action={
        <button onClick={() => m.mutate()} disabled={busy}
          className="text-[11.5px] font-medium text-muted hover:text-ink disabled:opacity-55">
          {busy ? "Searching…" : `Look them all up · $${price}`}
        </button>
      }
    >
      <ul className="flex flex-col">
        {live.map((a) => {
          const title = a.editions.find((e) => e.title)?.title || a.targetPhrase;
          const out = a.editions.map((e) => e.publishedAt).filter((d): d is string => Boolean(d)).sort()[0] ?? null;
          const place = a.serp ? yourPlace(a.serp, address) : null;
          const before = Boolean(a.serp && out && a.serp.readAt < out);
          return (
            <li key={a.id} className="flex items-baseline gap-3 py-1.5 border-b border-line last:border-b-0">
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-medium leading-snug">{title}</span>
                <span className="block text-[11.5px] text-muted">
                  {a.targetPhrase}
                  {a.serp ? ` · read ${new Date(a.serp.readAt).toLocaleDateString()}` : ""}
                  {before ? <span className="text-coral"> · before it went out</span> : null}
                </span>
              </span>
              <span className={`shrink-0 text-[12.5px] tabular-nums ${place ? "font-bold text-good" : "text-muted"}`}>
                {!a.serp ? "not looked" : place ? `#${place}` : "not in the ten"}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-[11.5px] text-faint leading-relaxed">
        Where one search put you on the day it was read, found by your address in Google's first ten. It needs no
        Google account. {seo.google?.property ? "Search Console's position is the average over a month; the two rarely match exactly." : "Search Console, if you connect it, adds how often you were shown and clicked."}
      </p>
    </Card>
  );
}

const Panel = ({ children }: { children: React.ReactNode }) => (
  <section className="rounded-xl border border-line bg-panel p-4">{children}</section>
);

function Connect(_: { onChanged: () => void }) {
  const m = useMutation({ mutationFn: () => api.connectSearchConsole(), onSuccess: (r) => { location.href = r.url; } });
  return (
    <Panel>
      <h2 className="text-[14px] font-semibold">Connect Google Search Console</h2>
      <p className="text-[12.5px] text-muted mt-1 leading-relaxed">
        The only numbers here that are not a guess: what people actually typed to find your site, which of your
        articles they saw, and where you come. Google gives an owner this for free.
      </p>
      <p className="text-[12.5px] text-muted mt-1 leading-relaxed">
        Joogoos asks to <b>read</b> and nothing else. It cannot change your site, your verification or your
        sitemap, and you can take it back from your Google account whenever you like.
      </p>
      <button onClick={() => m.mutate()} disabled={m.isPending}
        className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-ink text-white disabled:opacity-55">
        <Icon name="search" size={13} /> Connect Search Console
      </button>
      {m.error ? <p className="text-[12.5px] text-coral mt-1">{(m.error as Error).message}</p> : null}
    </Panel>
  );
}

/**
 * Which property. Chosen from what Google returns rather than typed: `sc-domain:overbill.co.uk` and
 * `https://overbill.co.uk/` are two different properties, and only the owner knows which one they verified.
 */
function PickProperty({ onChanged, account }: { onChanged: () => void; account: string | null }) {
  const q = useQuery({ queryKey: ["seo", "gsc-properties"], queryFn: () => api.searchProperties() });
  const m = useMutation({ mutationFn: (p: string) => api.chooseSearchProperty(p), onSuccess: onChanged });
  const choices = q.data?.choices ?? [];
  return (
    <Panel>
      <h2 className="text-[14px] font-semibold">Which site in Search Console?</h2>
      <p className="text-[12.5px] text-muted mt-0.5 leading-relaxed">
        Connected as {account ?? "your Google account"}. Pick the property you verified.
      </p>
      {q.isPending ? <p className="text-[12.5px] text-faint mt-1.5">Asking Google…</p> : null}
      {q.data?.error ? <p className="text-[12.5px] text-coral mt-1.5">{q.data.error}</p> : null}
      {!q.isPending && !choices.length ? (
        <p className="text-[12.5px] text-muted mt-1.5 leading-relaxed">
          Google says this account has verified no sites it can show data for. Verify your site in Search Console
          first, then come back.
        </p>
      ) : null}
      <ul className="mt-1.5 flex flex-col gap-1">
        {choices.map((c) => (
          <li key={c.property}>
            <button onClick={() => m.mutate(c.property)} disabled={m.isPending}
              className="w-full text-left px-3 py-2 rounded-lg bg-page hover:bg-line text-[13px] font-medium">
              {c.property}
            </button>
          </li>
        ))}
      </ul>
      <Disconnect onChanged={onChanged} />
    </Panel>
  );
}

function Numbers({ g, places, onChanged, onConnection }: {
  g: SearchConsoleView; places: React.ReactNode; onChanged: () => void; onConnection?: (() => void) | undefined;
}) {
  const pull = useMutation({ mutationFn: () => api.pullSearchConsole(), onSuccess: onChanged });
  return (
    <section className="flex flex-col gap-3">
      <Card
        title="What Google says"
        note={g.property}
        action={
          <button onClick={() => pull.mutate()} disabled={pull.isPending}
            className="text-[11.5px] font-medium text-muted hover:text-ink disabled:opacity-55">
            {pull.isPending ? "Asking…" : "Ask again"}
          </button>
        }
      >
        {g.error ? <p className="text-[12.5px] text-coral">{g.error}</p> : null}
        {!g.pulledAt ? <p className="text-[12.5px] text-muted">Nothing read yet — this fills in tonight, or press Ask again.</p> : (
          <>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-3">
              <Tile label="Clicks" value={g.clicks} />
              <Tile label="Times shown" value={g.impressions} />
              <Tile label="Phrases" value={g.queries.length} />
            </div>
            {/* Said every time, because a window that looks like it runs to today reads as a collapse. */}
            <p className="mt-2.5 text-[11.5px] text-faint">
              {g.windowDays} days up to {g.upTo}. Google publishes a few days behind, always.
            </p>
          </>
        )}
      </Card>

      {/* First, not third: this is the point of the section, and it is the only list in the product that is not a
          guess. What to do about it is named where it is read — the press itself stays in the one place it is on
          every half of the tab, rather than being drawn twice (21 §13). */}
      {g.opportunities.length ? (
        <section className="rounded-xl border border-coral/35 bg-coral/6 p-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="text-[13.5px] font-semibold">Worth writing next</h2>
            <span className="px-1.5 rounded-full bg-coral text-white text-[10px] font-bold tabular-nums">{g.opportunities.length}</span>
          </div>
          <p className="text-[12.5px] text-muted mt-1.5 leading-relaxed max-w-[62ch]">
            Google already shows your site for these, on page two or three, and almost nobody clicks. This is the
            one list here that is not a guess: you are being shown and passed over. <b>Plan topics</b>, at the top
            of this tab, starts from these phrases.
          </p>
          <Terms rows={g.opportunities} />
        </section>
      ) : null}

      {g.articles.length ? (
        <Card title="How your articles are doing">
          <Rows rows={g.articles.map((a) => ({ term: a.title || a.path, clicks: a.clicks, impressions: a.impressions, position: a.position }))} />
          <p className="mt-2 text-[11.5px] text-faint leading-relaxed">
            A new article shows nothing for a week or two: Google has to find it, then decide where it belongs.
          </p>
        </Card>
      ) : null}

      {places}

      {g.queries.length ? (
        <Card title="What people typed" note="every phrase this site was shown for">
          <Terms rows={g.queries} />
        </Card>
      ) : null}

      {/* The connection is kept where it is made, Business → SEO, beside the mailbox and the channels; here is only
          where it leads (moved 2026-09-30, when the owner looked for Disconnect in Business and it was here). */}
      <Panel>
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-muted">{g.account ? `Connected as ${g.account}. ` : ""}The connection is kept under Business → SEO.</span>
          {onConnection ? (
            <button onClick={onConnection} className="px-3 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium">Business → SEO ›</button>
          ) : null}
        </div>
      </Panel>
    </section>
  );
}

const Tile = ({ label, value }: { label: string; value: number }) => (
  <div className="rounded-lg bg-page border border-line px-3 py-2">
    <div className="text-[10.5px] font-semibold uppercase tracking-[.06em] text-muted">{label}</div>
    <div className="font-display text-[26px] font-bold tabular-nums leading-tight tracking-[-.02em]">{value.toLocaleString()}</div>
  </div>
);

const Terms = ({ rows }: { rows: SearchTermView[] }) => <Rows rows={rows} />;

function Rows({ rows }: { rows: SearchTermView[] }) {
  return (
    <table className="w-full mt-1.5 text-[12.5px] border-separate border-spacing-0">
      <thead>
        <tr className="text-[10px] font-bold uppercase tracking-[.06em] text-muted text-left">
          <th className="border-b border-line pb-1 font-bold">Phrase</th>
          <th className="border-b border-line pb-1 font-bold text-right">Shown</th>
          <th className="border-b border-line pb-1 font-bold text-right">Clicks</th>
          <th className="border-b border-line pb-1 font-bold text-right">Position</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.term}>
            <td className="border-b border-line py-1.5 pr-3">{r.term}</td>
            <td className="border-b border-line py-1.5 text-right tabular-nums">{r.impressions.toLocaleString()}</td>
            <td className="border-b border-line py-1.5 text-right tabular-nums">{r.clicks.toLocaleString()}</td>
            <td className="border-b border-line py-1.5 text-right tabular-nums">{r.position ? r.position.toFixed(1) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Disconnect({ onChanged, account }: { onChanged: () => void; account?: string | null }) {
  const [asking, setAsking] = useState(false);
  const m = useMutation({ mutationFn: () => api.disconnectSearchConsole(), onSuccess: () => { setAsking(false); onChanged(); } });
  return (
    // A button that looks like one: a grey word at the foot of the page was looked for and not found (2026-09-30).
    <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
      {account ? <span className="text-muted">Connected as {account}.</span> : null}
      {asking ? (
        <>
          {/* Google keeps one grant for everything Joogoos connects with an account, so it goes back with the last of them (ADR 0059). */}
          <span className="text-ink">Disconnect? Google gets the permission back once nothing else in Joogoos uses this account.</span>
          <button onClick={() => m.mutate()} disabled={m.isPending} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-coral text-white disabled:opacity-55">Disconnect</button>
          <button onClick={() => setAsking(false)} className="text-muted hover:text-ink">Keep it</button>
        </>
      ) : (
        <button onClick={() => setAsking(true)} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-line-2 text-ink hover:border-coral hover:text-coral">
          Disconnect Search Console
        </button>
      )}
    </div>
  );
}
